import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { applicationLogger } from "@frontend/shared/infrastructure/logger/application-logger.singleton";
import { useRequiredEventRouteContext } from "@/shared/presentation/hooks/useEventRouteContext";
import { useDerivedState } from "@frontend/shared/presentation/hooks/useDerivedState";
import { useUserActions } from "@frontend/shared/features/actions/application/actions/use-user-actions";
import {
  DirectoryTab,
  DirectoryTabSchema,
  type DirectoryTabType,
} from "../../domain/types/tab.types";
import {
  SEARCH_GROUPS,
  SearchGroup,
  type SearchGroupType,
} from "../../domain/types/search-group.types";
import {
  EventIdPublic,
  EventIdPublicOrSlug,
  EventParticipantIdPublic,
} from "@shared/schemas/branded-id.schemas";
import { IntroRequestStatus } from "@shared/schemas/intro-request-state.schemas";
import { resolveParticipantDisplayNameFromSource } from "@shared/utils/participant-name.utils";
import { FeatureKey } from "@shared/schemas/feature-key.schemas";
import { DirectorySearchMode } from "@shared/schemas/directory-search-mode.schemas";
import type { DirectoryCard } from "@frontend/shared/domain/types/directory-card.types";
import type { TopPick } from "@frontend/shared/domain/types/top-pick.types";
import type { ConnectionRequestSummary } from "@shared/schemas/connection-request.schemas";
import type { ThemeModeType } from "@shared/types/theme-mode.types";
import type { EventPublicView } from "@shared/schemas/event.schemas";
import { useFeatureConfigs } from "@frontend/shared/features/feature-config/application/hooks/use-feature-configs";
import type { Suggestion } from "@shared/schemas/suggestion.schemas";

export interface PrefetchState {
  readonly isPrefetching: boolean;

  readonly isComplete: boolean;
}

export interface DirectoryControllerState {
  readonly eventIdPublic: string;

  readonly activeTab: DirectoryTabType;
  readonly searchQuery: string;

  readonly isBannerDismissed: boolean;
  readonly isGeneratingMore: boolean;

  readonly showSentRequests: boolean;

  readonly event: EventPublicView | undefined;
  readonly allEntries: readonly DirectoryCard[] | undefined;
  readonly totalEntriesCount: number | undefined;

  readonly totalEntriesCountForBanner: number | undefined;
  readonly filteredEntries: readonly DirectoryCard[];
  /** Search answers in two groups: names (`filteredEntries`) and meaning. */
  readonly isNameGroupShown: boolean;
  readonly isMeaningGroupShown: boolean;
  readonly meaningEntries: readonly DirectoryCard[];
  readonly expandedSearchGroups: readonly SearchGroupType[];
  readonly topPicks: readonly TopPick[] | undefined;
  readonly totalTopPicksCount: number;
  /**
   * Whether a matching run that covers this reader is queued or executing, so
   * the picks above are the previous set rather than the current one. False
   * while the suggestions query is disabled or still loading: the skeleton already
   * stands in for "not known yet".
   */
  readonly isRecalculationPending: boolean;
  readonly connectionRequests: readonly ConnectionRequestSummary[] | undefined;
  readonly sentRequests: readonly ConnectionRequestSummary[] | undefined;
  readonly connectedProfiles: readonly DirectoryCard[] | undefined;
  readonly requestCount: number;

  readonly isLoading: boolean;
  readonly isEventLoading: boolean;
  readonly isAllEntriesLoading: boolean;
  readonly isTopPicksLoading: boolean;
  readonly isRequestsLoading: boolean;
  readonly isSentRequestsLoading: boolean;
  readonly isConnectedLoading: boolean;
  readonly isRetrying: boolean;
  readonly isFetchingNextPage: boolean;
  readonly isSearchPending: boolean;
  readonly isMeaningSearchPending: boolean;
  readonly isSearchInFlight: boolean;

  readonly hasNextPage: boolean;
  readonly hasMoreTopPicks: boolean;

  readonly error: Error | null;

  readonly themeMode: ThemeModeType | undefined;

  readonly currentUserParticipantIdPublic: string | null;

  readonly viewerPhotoUrl: string | null;
  readonly viewerDisplayName: string;

  readonly prefetch: PrefetchState;

  readonly isAiRecommendationsActive: boolean;

  readonly isIsolatedGateActive: boolean;
  readonly isOptingIn: boolean;
  readonly optInError: Error | null;
}

export interface DirectoryControllerActions {
  readonly setActiveTab: (tab: DirectoryTabType) => void;
  readonly setSearchQuery: (query: string) => void;
  readonly expandSearchGroup: (group: SearchGroupType) => void;
  readonly syncSearchQueryToUrl: (query: string) => void;
  readonly dismissBanner: () => void;
  readonly handleProfileClick: (
    participantIdPublic: EventParticipantIdPublic,
  ) => void;
  readonly handleTopPickClick: (
    participantIdPublic: EventParticipantIdPublic,
  ) => void;
  readonly handleGenerateMore: () => Promise<void>;

  readonly handleEditOwnProfile: (() => void) | undefined;
  readonly retry: () => void;
  readonly fetchNextPage: () => void;

  readonly setShowSentRequests: (show: boolean) => void;
  readonly onOptInRemember: () => Promise<void>;
}

export interface DirectoryControllerResult {
  readonly state: DirectoryControllerState;
  readonly actions: DirectoryControllerActions;
}

const SEARCH_PARAM_TAB = "tab";
const SEARCH_PARAM_QUERY = "q";
const SEARCH_PARAM_SENT = "sent";
const GENERATE_MORE_DELAY_MS = 500;
const TOP_PICKS_PAGE_SIZE = 5;

type ExpandedSearchQueries = Readonly<Record<SearchGroupType, string | null>>;

function expandedSearchGroupKey(
  eventIdPublic: EventIdPublic,
  group: SearchGroupType,
): string {
  return `directory:${EventIdPublic.unwrap(eventIdPublic)}:search-${group}-expanded`;
}

// Session-stored because opening a profile unmounts the directory.
function readExpandedSearchQueries(
  eventIdPublic: EventIdPublic,
): ExpandedSearchQueries {
  const read = (group: SearchGroupType): string | null =>
    sessionStorage.getItem(expandedSearchGroupKey(eventIdPublic, group));
  return {
    [SearchGroup.NAMES]: read(SearchGroup.NAMES),
    [SearchGroup.MEANING]: read(SearchGroup.MEANING),
  };
}

const ACTIVE_REQUEST_STATUSES: readonly string[] =
  IntroRequestStatus.ACTIVE_STATES;
const TOP_PICKS_VISIBLE_COUNT_KEY_PREFIX = "directory_top_picks_visible_";

// suggestion targets are missing from the paginated cache.
const EMPTY_TARGET_IDS: readonly EventParticipantIdPublic[] = [];

// Must match the backend `DirectoryEntriesBatchBodySchema` cap.
const MAX_BATCH_TARGET_IDS = 100;

function getTopPicksStorageKey(eventIdPublic: string): string {
  return `${TOP_PICKS_VISIBLE_COUNT_KEY_PREFIX}${eventIdPublic}`;
}

function loadVisibleTopPicksCount(eventIdPublic: string): number {
  try {
    const stored = localStorage.getItem(getTopPicksStorageKey(eventIdPublic));
    if (stored !== null) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed >= TOP_PICKS_PAGE_SIZE) {
        return parsed;
      }
    }
  } catch {
    // localStorage not available, use default
  }
  return TOP_PICKS_PAGE_SIZE;
}

function saveVisibleTopPicksCount(eventIdPublic: string, count: number): void {
  try {
    localStorage.setItem(
      getTopPicksStorageKey(eventIdPublic),
      count.toString(),
    );
  } catch {
    // localStorage not available, ignore
  }
}

// eslint-disable-next-line complexity -- Controller orchestrates multiple features; complexity is inherent
export function useDirectoryController(): DirectoryControllerResult {
  const eventContext = useRequiredEventRouteContext();
  const eventIdPublic = eventContext.eventIdPublic;
  const eventQuery = {
    data: eventContext.event,
    isLoading: eventContext.isLoading,
  };
  const nav = eventContext.nav;
  const areQueriesEnabled = EventIdPublic.unwrap(eventIdPublic).length > 0;

  const userActions = useUserActions();
  const eventActions = userActions.useForEvent({
    eventIdPublicOrSlug: EventIdPublicOrSlug.unsafeCreate(
      eventContext.eventIdPublicOrSlug,
    ),
  });
  const participantActions = eventActions.useForParticipant({ self: true });
  const mutations = participantActions.mutations;

  const [searchParams, setSearchParams] = useSearchParams();

  // Read before the tab default below: which tab the directory opens on depends
  // on whether this event has suggestions at all. Both inputs are already
  // resolved — eventIdPublic and areQueriesEnabled are set above.
  const {
    isFeatureEnabled,
    getFeatureConfig,
    isLoading: isFeatureConfigLoading,
  } = useFeatureConfigs({
    context: { eventIdPublic: eventIdPublic },
    enabled: areQueriesEnabled,
  });

  const aiRecommendationsConfig = getFeatureConfig(
    FeatureKey.AI_RECOMMENDATIONS,
  );
  const isInactiveForIsolated =
    aiRecommendationsConfig?.config.inactiveForIsolated === true &&
    eventContext.currentParticipant.isolated === true;
  const isAiRecommendationsActive =
    isFeatureEnabled(FeatureKey.AI_RECOMMENDATIONS) === true &&
    aiRecommendationsConfig?.config.active === true &&
    !isInactiveForIsolated;
  const isIsolatedGateActive =
    aiRecommendationsConfig?.config.gateForIsolated === true &&
    eventContext.currentParticipant.isolated === true;

  const tabParam = searchParams.get(SEARCH_PARAM_TAB) ?? "";
  // Suggestions is the point of the product, so it is where the directory opens
  // when the event has them. Events with recommendations off — and isolated
  // participants under inactiveForIsolated — never mount that tab, so they keep
  // landing on All. An explicit ?tab= still wins, so deep links are unaffected.
  const defaultTab: DirectoryTabType = isAiRecommendationsActive
    ? DirectoryTab.TOP_PICKS
    : DirectoryTab.DEFAULT;
  const activeTab: DirectoryTabType =
    DirectoryTabSchema.safeParse(tabParam).data ?? defaultTab;

  const searchQueryFromUrl = searchParams.get(SEARCH_PARAM_QUERY) ?? "";

  // Deep-link: ?tab=requests&sent=true opens sent requests overlay (PLA-152)
  const sentParam = searchParams.get(SEARCH_PARAM_SENT);

  const [searchQuery, setSearchQueryLocal] =
    useDerivedState(searchQueryFromUrl);
  const [expandedSearchQueries, setExpandedSearchQueries] = useState(() =>
    readExpandedSearchQueries(eventIdPublic),
  );
  const expandedSearchGroups = SEARCH_GROUPS.filter(
    (group) => expandedSearchQueries[group] === searchQuery,
  );

  const currentUserParticipantIdPublic =
    participantActions.participant?.idPublic ?? null;
  const isParticipantLoading = participantActions.isLoading;
  const participantError = participantActions.error;

  const [isBannerDismissed, setIsBannerDismissed] = useState(false);
  const [isGeneratingMore, setIsGeneratingMore] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [showSentRequests, setShowSentRequests] = useState(
    sentParam === "true",
  );

  const [visibleTopPicksCount, setVisibleTopPicksCount] = useState(() =>
    loadVisibleTopPicksCount(EventIdPublic.unwrap(eventIdPublic)),
  );

  const [sourceTabBeforeSentRequests, setSourceTabBeforeSentRequests] =
    useState<DirectoryTabType | null>(sentParam === "true" ? activeTab : null);

  useEffect(() => {
    if (EventIdPublic.unwrap(eventIdPublic).length > 0) {
      saveVisibleTopPicksCount(
        EventIdPublic.unwrap(eventIdPublic),
        visibleTopPicksCount,
      );
    }
  }, [eventIdPublic, visibleTopPicksCount]);

  const effectiveSearchQuery = useMemo(() => {
    const trimmed = searchQuery.trim();
    return trimmed.length >= 2 ? trimmed : undefined;
  }, [searchQuery]);

  // Meaning matches take seconds to rank, literal ones are immediate. Asking
  // for them apart lets the literal half show while the meaning half is ranked.
  const isSearch = effectiveSearchQuery !== undefined;

  const allEntriesQuery = eventActions.useGetDirectoryEntries({
    eventIdPublic: eventIdPublic,
    ...(effectiveSearchQuery !== undefined && {
      searchQuery: effectiveSearchQuery,
    }),
    ...(isSearch && { searchMode: DirectorySearchMode.text }),
    enabled: areQueriesEnabled,
  });

  const meaningEntriesQuery = eventActions.useGetDirectoryEntries({
    eventIdPublic: eventIdPublic,
    ...(effectiveSearchQuery !== undefined && {
      searchQuery: effectiveSearchQuery,
    }),
    searchMode: DirectorySearchMode.vector,
    enabled: areQueriesEnabled && isSearch,
  });

  const optInRememberMutation = mutations?.optInRemember;
  const isOptingIn = optInRememberMutation?.isPending ?? false;
  const [optInError, setOptInError] = useState<Error | null>(null);

  const suggestionsParticipantId =
    currentUserParticipantIdPublic ?? EventParticipantIdPublic.unsafeCreate("");
  const suggestionsQuery = participantActions.useGetSuggestions({
    eventIdPublic: eventIdPublic,
    participantIdPublic: suggestionsParticipantId,
    enabled:
      areQueriesEnabled &&
      currentUserParticipantIdPublic !== null &&
      isAiRecommendationsActive,
  });

  const missingTargetIds = useMemo((): readonly EventParticipantIdPublic[] => {
    if (suggestionsQuery.data === undefined) {
      return EMPTY_TARGET_IDS;
    }
    const loadedIds = new Set<EventParticipantIdPublic>();
    if (allEntriesQuery.data !== undefined) {
      for (const page of allEntriesQuery.data.pages) {
        for (const entry of page.items) {
          loadedIds.add(entry.participantIdPublic);
        }
      }
    }
    const missing = new Set<EventParticipantIdPublic>();
    for (const suggestion of suggestionsQuery.data.items) {
      if (!loadedIds.has(suggestion.targetParticipantIdPublic)) {
        missing.add(suggestion.targetParticipantIdPublic);
      }
    }
    if (missing.size === 0) {
      return EMPTY_TARGET_IDS;
    }
    if (missing.size > MAX_BATCH_TARGET_IDS) {
      applicationLogger.warn(
        "Capping AI Picks batch fetch at backend limit; some targets will not resolve",
        { missing: missing.size, cap: MAX_BATCH_TARGET_IDS },
      );
      return Array.from(missing).slice(0, MAX_BATCH_TARGET_IDS);
    }
    return Array.from(missing);
  }, [suggestionsQuery.data, allEntriesQuery.data]);

  const missingEntriesBatchQuery = eventActions.useGetDirectoryEntriesBatch({
    eventIdPublic: eventIdPublic,
    participantIdsPublic: missingTargetIds,
    enabled:
      areQueriesEnabled &&
      activeTab === DirectoryTab.TOP_PICKS &&
      isAiRecommendationsActive,
  });

  const requestsQuery = participantActions.useGetConnectionRequests({
    eventIdPublic: eventIdPublic,
    direction: "received",
    enabled: areQueriesEnabled,
  });

  const sentRequestsQuery = participantActions.useGetConnectionRequests({
    eventIdPublic: eventIdPublic,
    direction: "sent",
    enabled:
      areQueriesEnabled &&
      (showSentRequests || activeTab === DirectoryTab.REQUESTS),
  });

  const connectedQuery = participantActions.useGetConnectedProfiles({
    eventIdPublic: eventIdPublic,
    enabled: areQueriesEnabled && activeTab === DirectoryTab.CONNECTED,
  });

  const infiniteScrollEntries = useMemo(
    () => allEntriesQuery.data?.pages.flatMap((page) => page.items),
    [allEntriesQuery.data],
  );

  // Meaning only adds the people the literal half did not already name:
  // someone typing a name has said exactly who they want.
  const meaningEntries = useMemo((): readonly DirectoryCard[] => {
    if (!isSearch || meaningEntriesQuery.data === undefined) {
      return [];
    }
    const shown = new Set(
      (infiniteScrollEntries ?? []).map((entry) => entry.participantIdPublic),
    );
    return meaningEntriesQuery.data.pages
      .flatMap((page) => page.items)
      .filter((entry) => !shown.has(entry.participantIdPublic));
  }, [infiniteScrollEntries, isSearch, meaningEntriesQuery.data]);

  const isMeaningSearchPending = isSearch && meaningEntriesQuery.isFetching;

  // The previous results stay on screen while a new query loads, so the
  // list alone gives no sign that anything is happening.
  const isSearchPending =
    effectiveSearchQuery !== undefined &&
    allEntriesQuery.isFetching &&
    !allEntriesQuery.isFetchingNextPage;

  const totalEntriesCount = useMemo(() => {
    const firstPage = allEntriesQuery.data?.pages[0];
    return firstPage?.total;
  }, [allEntriesQuery.data]);

  const [totalEntriesCountForBanner, setTotalEntriesCountForBanner] = useState<
    number | undefined
  >(undefined);
  useEffect(() => {
    if (effectiveSearchQuery === undefined && totalEntriesCount !== undefined) {
      setTotalEntriesCountForBanner(totalEntriesCount); // eslint-disable-line react-hooks/set-state-in-effect -- deliberate sync: capture unfiltered total before search changes the query key
    }
  }, [effectiveSearchQuery, totalEntriesCount]);

  const smartPrefetchConfig = getFeatureConfig(
    FeatureKey.DIRECTORY_SMART_PREFETCH,
  );
  const isSmartPrefetchEnabled =
    isFeatureEnabled(FeatureKey.DIRECTORY_SMART_PREFETCH) === true;

  const prefetchResult = eventActions.usePrefetchAllDirectoryPages({
    entriesQuery: allEntriesQuery,
    enabled:
      areQueriesEnabled &&
      activeTab === DirectoryTab.ALL &&
      effectiveSearchQuery === undefined &&
      !isFeatureConfigLoading &&
      isSmartPrefetchEnabled,
    delayMs: smartPrefetchConfig?.config.delayMs ?? 2000,
    maxPages:
      smartPrefetchConfig?.config.maxEntries !== undefined &&
      smartPrefetchConfig.config.maxEntries > 0
        ? Math.ceil(smartPrefetchConfig.config.maxEntries / 100)
        : 0,
  });

  const prefetchState: PrefetchState = prefetchResult;

  // Entries to display - backend returns pre-sorted by last name (PLA-442)
  const filteredEntries = infiniteScrollEntries ?? [];

  // An empty group gives the screen to the other; with both empty the empty
  // state waits until both have answered. Past two letters a name match means
  // the reader typed a name, so meaning loads unseen and shows only if found.
  const hasNameMatches = !isSearchPending && filteredEntries.length > 0;
  const isMeaningLoadQuiet =
    hasNameMatches && (effectiveSearchQuery?.length ?? 0) > 2;
  const isNameGroupShown =
    isSearch && (isSearchPending || filteredEntries.length > 0);
  const isMeaningGroupShown =
    isSearch &&
    (isMeaningSearchPending ? !isMeaningLoadQuiet : meaningEntries.length > 0);

  const allEntries = infiniteScrollEntries;

  // Connected profiles - backend returns pre-sorted by last name (PLA-442)
  const sortedConnectedProfiles = connectedQuery.data;

  const sortedConnectionRequests = useMemo(() => {
    const requests = requestsQuery.data;
    if (requests === undefined || requests.length === 0) {
      return requests;
    }
    return [...requests].sort((a, b) =>
      a.fromUserName.localeCompare(b.fromUserName),
    );
  }, [requestsQuery.data]);

  const sortedSentRequests = useMemo(() => {
    const requests = sentRequestsQuery.data;
    if (requests === undefined || requests.length === 0) {
      return requests;
    }
    return [...requests].sort((a, b) =>
      a.toUserName.localeCompare(b.toUserName),
    );
  }, [sentRequestsQuery.data]);

  const themeMode = useMemo((): ThemeModeType | undefined => {
    const themeConfig = getFeatureConfig(FeatureKey.EVENT_THEME);
    if (themeConfig === undefined || !themeConfig.enabled) {
      return undefined;
    }
    return themeConfig.config.themeMode;
  }, [getFeatureConfig]);

  const requestCount = useMemo(
    () =>
      requestsQuery.data?.filter((r) =>
        ACTIVE_REQUEST_STATUSES.includes(r.typeData.status),
      ).length ?? 0,
    [requestsQuery.data],
  );

  const topPicksFromSuggestions = useMemo(():
    | readonly TopPick[]
    | undefined => {
    if (suggestionsQuery.data === undefined) {
      return undefined;
    }

    const entriesMap = new Map<EventParticipantIdPublic, DirectoryCard>();
    if (allEntriesQuery.data !== undefined) {
      for (const page of allEntriesQuery.data.pages) {
        for (const entry of page.items) {
          entriesMap.set(entry.participantIdPublic, entry);
        }
      }
    }
    if (missingEntriesBatchQuery.data !== undefined) {
      for (const entry of missingEntriesBatchQuery.data) {
        if (!entriesMap.has(entry.participantIdPublic)) {
          entriesMap.set(entry.participantIdPublic, entry);
        }
      }
    }

    const grouped = new Map<
      EventParticipantIdPublic,
      { entry: DirectoryCard; suggestions: Suggestion[] }
    >();
    for (const suggestion of suggestionsQuery.data.items) {
      const entry = entriesMap.get(suggestion.targetParticipantIdPublic);
      if (entry === undefined) {
        // Race window: batch query still in flight (or transient network error).
        continue;
      }
      const key = suggestion.targetParticipantIdPublic;
      const existing = grouped.get(key);
      if (existing !== undefined) {
        existing.suggestions.push(suggestion);
      } else {
        grouped.set(key, { entry, suggestions: [suggestion] });
      }
    }

    return Array.from(grouped.values())
      .map(({ entry, suggestions }) => ({ entry, suggestions }))
      .sort((a, b) => {
        const rankA = a.suggestions[0]?.rank ?? Number.MAX_SAFE_INTEGER;
        const rankB = b.suggestions[0]?.rank ?? Number.MAX_SAFE_INTEGER;
        return rankA - rankB;
      });
  }, [
    suggestionsQuery.data,
    allEntriesQuery.data,
    missingEntriesBatchQuery.data,
  ]);

  const visibleTopPicks = useMemo(() => {
    if (topPicksFromSuggestions === undefined) {
      return undefined;
    }
    return topPicksFromSuggestions.slice(0, visibleTopPicksCount);
  }, [topPicksFromSuggestions, visibleTopPicksCount]);

  const hasMoreTopPicks = useMemo(() => {
    if (topPicksFromSuggestions === undefined) {
      return false;
    }
    return visibleTopPicksCount < topPicksFromSuggestions.length;
  }, [topPicksFromSuggestions, visibleTopPicksCount]);

  const isLoading =
    isParticipantLoading ||
    allEntriesQuery.isLoading ||
    (activeTab === DirectoryTab.TOP_PICKS &&
      (suggestionsQuery.isLoading || missingEntriesBatchQuery.isLoading)) ||
    (activeTab === DirectoryTab.REQUESTS && requestsQuery.isLoading) ||
    (activeTab === DirectoryTab.CONNECTED && connectedQuery.isLoading);

  const error = useMemo((): Error | null => {
    if (participantError !== null) {
      return participantError;
    }

    if (allEntriesQuery.error !== null) {
      return allEntriesQuery.error;
    }

    switch (activeTab) {
      case DirectoryTab.ALL:
        return null;
      case DirectoryTab.TOP_PICKS:
        return suggestionsQuery.error ?? missingEntriesBatchQuery.error ?? null;
      case DirectoryTab.REQUESTS:
        return requestsQuery.error ?? sentRequestsQuery.error ?? null;
      case DirectoryTab.CONNECTED:
        return connectedQuery.error ?? null;
      default:
        return null;
    }
  }, [
    activeTab,
    participantError,
    allEntriesQuery.error,
    suggestionsQuery.error,
    missingEntriesBatchQuery.error,
    requestsQuery.error,
    sentRequestsQuery.error,
    connectedQuery.error,
  ]);

  const setActiveTab = useCallback(
    (tab: DirectoryTabType) => {
      // Always written, never dropped for the default. Dropping it made the
      // selected tab depend on which tab happens to be default: selecting the
      // default cleared the param, and the next resolution re-derived it from
      // the feature flag. It also makes a shared link mean the same thing for
      // the recipient as it did for the sender, whatever their event allows.
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(SEARCH_PARAM_TAB, tab);
        return next;
      });
    },
    [setSearchParams],
  );

  const setSearchQueryAction = useCallback(
    (query: string) => {
      setSearchQueryLocal(query);
      // Only the latest search term keeps its See all.
      for (const group of SEARCH_GROUPS) {
        const key = expandedSearchGroupKey(eventIdPublic, group);
        if (sessionStorage.getItem(key) !== query) {
          sessionStorage.removeItem(key);
        }
      }
      setExpandedSearchQueries(readExpandedSearchQueries(eventIdPublic));
    },
    [setSearchQueryLocal, eventIdPublic],
  );

  const expandSearchGroup = useCallback(
    (group: SearchGroupType) => {
      sessionStorage.setItem(
        expandedSearchGroupKey(eventIdPublic, group),
        searchQuery,
      );
      setExpandedSearchQueries(readExpandedSearchQueries(eventIdPublic));
    },
    [eventIdPublic, searchQuery],
  );

  const syncSearchQueryToUrl = useCallback(
    (query: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (query !== "") {
            next.set(SEARCH_PARAM_QUERY, query);
          } else {
            next.delete(SEARCH_PARAM_QUERY);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const dismissBanner = useCallback(() => {
    setIsBannerDismissed(true);
  }, []);

  const handleProfileClick = useCallback(
    (participantIdPublic: EventParticipantIdPublic): void => {
      nav.toParticipantProfile(participantIdPublic);
    },
    [nav],
  );

  const handleTopPickClick = useCallback(
    (participantIdPublic: EventParticipantIdPublic): void => {
      nav.toParticipantProfileWhyConnect(participantIdPublic);
    },
    [nav],
  );

  const handleGenerateMore = useCallback(async (): Promise<void> => {
    setIsGeneratingMore(true);

    await new Promise((resolve) => setTimeout(resolve, GENERATE_MORE_DELAY_MS));

    setVisibleTopPicksCount((prev) => prev + TOP_PICKS_PAGE_SIZE);

    setIsGeneratingMore(false);
  }, []);

  const optInRememberAsync = optInRememberMutation?.mutateAsync;
  const onOptInRemember = useCallback(async (): Promise<void> => {
    if (
      currentUserParticipantIdPublic === null ||
      optInRememberAsync === undefined
    ) {
      return;
    }
    setOptInError(null);
    try {
      await optInRememberAsync({
        eventIdPublic: eventIdPublic,
        participantIdPublic: currentUserParticipantIdPublic,
      });
    } catch (error: unknown) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      applicationLogger.error(
        "Opt-in remember failed",
        "useDirectoryController.onOptInRemember",
        errorObj,
        [{ eventIdPublic: EventIdPublic.unwrap(eventIdPublic) }],
      );
      setOptInError(errorObj);
    }
  }, [optInRememberAsync, eventIdPublic, currentUserParticipantIdPublic]);

  const retry = useCallback((): void => {
    setIsRetrying(true);

    const refetchPromises: Promise<unknown>[] = [];

    if (allEntriesQuery.error !== null) {
      refetchPromises.push(allEntriesQuery.refetch());
    }
    if (suggestionsQuery.error !== null) {
      refetchPromises.push(suggestionsQuery.refetch());
    }
    if (requestsQuery.error !== null) {
      refetchPromises.push(requestsQuery.refetch());
    }
    if (connectedQuery.error !== null) {
      refetchPromises.push(connectedQuery.refetch());
    }

    Promise.all(refetchPromises)
      .catch((retryError: Error) => {
        applicationLogger.error(
          "Retry failed",
          "useDirectoryController.retry",
          retryError,
          [{ eventIdPublic: EventIdPublic.unwrap(eventIdPublic) }],
        );
      })
      .finally(() => {
        setIsRetrying(false);
      });
  }, [
    allEntriesQuery,
    suggestionsQuery,
    requestsQuery,
    connectedQuery,
    eventIdPublic,
  ]);

  useEffect(() => {
    if (
      activeTab === DirectoryTab.TOP_PICKS &&
      !isAiRecommendationsActive &&
      !isFeatureConfigLoading
    ) {
      setActiveTab(DirectoryTab.ALL);
    }
  }, [
    activeTab,
    isAiRecommendationsActive,
    isFeatureConfigLoading,
    setActiveTab,
  ]);

  useEffect(() => {
    if (activeTab === DirectoryTab.REQUESTS) {
      void requestsQuery.refetch();
      void sentRequestsQuery.refetch();
    } else if (activeTab === DirectoryTab.CONNECTED) {
      void connectedQuery.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only refetch on tab change, not on query changes
  }, [activeTab]);

  const handleEditOwnProfile = useMemo((): (() => void) | undefined => {
    if (currentUserParticipantIdPublic === null) {
      return undefined;
    }
    return (): void => {
      nav.toProfileEdit();
    };
  }, [nav, currentUserParticipantIdPublic]);

  const fetchNextPage = useCallback((): void => {
    if (allEntriesQuery.hasNextPage && !allEntriesQuery.isFetchingNextPage) {
      void allEntriesQuery.fetchNextPage();
    }
  }, [allEntriesQuery]);

  const handleSetShowSentRequests = useCallback(
    (show: boolean): void => {
      if (show) {
        setSourceTabBeforeSentRequests(activeTab);
        setShowSentRequests(true);
        // Sync sent=true to URL so browser history preserves overlay state (PLA-435)
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set(SEARCH_PARAM_SENT, "true");
          return next;
        });
      } else {
        setShowSentRequests(false);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete(SEARCH_PARAM_SENT);
          if (sourceTabBeforeSentRequests !== null) {
            if (sourceTabBeforeSentRequests === DirectoryTab.DEFAULT) {
              next.delete(SEARCH_PARAM_TAB);
            } else {
              next.set(SEARCH_PARAM_TAB, sourceTabBeforeSentRequests);
            }
          }
          return next;
        });
        setSourceTabBeforeSentRequests(null);
      }
    },
    [activeTab, sourceTabBeforeSentRequests, setSearchParams],
  );

  return {
    state: {
      eventIdPublic: EventIdPublic.unwrap(eventIdPublic),
      activeTab,
      searchQuery,
      isBannerDismissed,
      isGeneratingMore,
      showSentRequests,
      event: eventQuery.data,
      allEntries,
      totalEntriesCount,
      totalEntriesCountForBanner,
      filteredEntries,
      isNameGroupShown,
      isMeaningGroupShown,
      meaningEntries,
      expandedSearchGroups,
      topPicks: visibleTopPicks,
      totalTopPicksCount: topPicksFromSuggestions?.length ?? 0,
      isRecalculationPending:
        suggestionsQuery.data?.recalculationPending ?? false,
      connectionRequests: sortedConnectionRequests,
      sentRequests: sortedSentRequests,
      connectedProfiles: sortedConnectedProfiles,
      requestCount,
      isLoading,
      isEventLoading: eventQuery.isLoading,
      isAllEntriesLoading: allEntriesQuery.isLoading,
      isTopPicksLoading:
        suggestionsQuery.isLoading || missingEntriesBatchQuery.isLoading,
      isRequestsLoading: requestsQuery.isLoading,
      isSentRequestsLoading: sentRequestsQuery.isLoading,
      isConnectedLoading: connectedQuery.isLoading,
      isRetrying,
      isFetchingNextPage: allEntriesQuery.isFetchingNextPage,
      isSearchPending,
      isMeaningSearchPending,
      isSearchInFlight: isSearchPending || isMeaningSearchPending,
      hasNextPage: allEntriesQuery.hasNextPage,
      hasMoreTopPicks,
      error,
      themeMode,
      currentUserParticipantIdPublic:
        currentUserParticipantIdPublic === null
          ? null
          : EventParticipantIdPublic.unwrap(currentUserParticipantIdPublic),
      viewerPhotoUrl: participantActions.participant?.photoUrl?.value ?? null,
      viewerDisplayName:
        participantActions.participant === undefined
          ? ""
          : resolveParticipantDisplayNameFromSource(
              participantActions.participant,
            ),
      prefetch: prefetchState,
      isAiRecommendationsActive,
      isIsolatedGateActive,
      isOptingIn,
      optInError,
    },
    actions: {
      setActiveTab,
      setSearchQuery: setSearchQueryAction,
      expandSearchGroup,
      syncSearchQueryToUrl,
      dismissBanner,
      handleProfileClick,
      handleTopPickClick,
      handleGenerateMore,
      handleEditOwnProfile,
      retry,
      fetchNextPage,
      setShowSentRequests: handleSetShowSentRequests,
      onOptInRemember,
    },
  };
}
